(() => {
  if (window.TFOCartGuard) return;

  /** ========================
   *  Config & constants
   * =======================*/
  const REQUIRED_ATTRIBUTE = 'pickupLocation';
  const REDIRECT_URL = '/pages/pickuplocation';
  const VERSION = '1.1.0'; // refactored
  const GUARD_BLOCKED = 'blocked';

  /** ========================
   *  Native refs & state
   * =======================*/
  const guardState = { skipNextIntercept: false };
  const nativeFetch =
    typeof window.fetch === 'function' ? window.fetch.bind(window) : null;
  const nativeXHROpen = XMLHttpRequest.prototype.open;
  const nativeXHRSend = XMLHttpRequest.prototype.send;

  /** ========================
   *  Utils
   * =======================*/
  const toStr = (v) => (typeof v === 'string' ? v : '');
  const isPost = (m) => (m || 'GET').toUpperCase() === 'POST';

  function safeEmit(eventName, detail) {
    try {
      window.dispatchEvent(new CustomEvent(eventName, { detail }));
    } catch (err) {
      console.warn('Cart guard event dispatch failed', err);
    }
  }

  function createBlockError() {
    const error = new Error('Required cart attribute missing');
    error.__tfoGuard = GUARD_BLOCKED;
    return error;
  }

  function getUrlFromInput(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input === 'object') {
      if (typeof input.url === 'string') return input.url;
      if (typeof input.href === 'string') return input.href;
    }
    return '';
  }

  function getMethodFromInputs(input, init) {
    const m =
      (init && init.method) ||
      (input && input.method) ||
      'GET';
    return String(m).toUpperCase();
  }

  function shouldIntercept(url, method) {
    const u = toStr(url);
    if (!u || !isPost(method)) return false;

    // URL を安定して判定（相対/絶対どちらでもOK）
    let path = '';
    try {
      path = new URL(u, location.origin).pathname;
    } catch {
      // 万一 URL 生成に失敗しても従来の includes でフォールバック
      return u.includes('/cart/add');
    }
    return path.includes('/cart/add');
  }

  /** ========================
   *  Cart loaders
   * =======================*/
  function getCartWithFetch() {
    if (!nativeFetch) {
      return Promise.reject(new Error('Fetch is unavailable'));
    }
    return nativeFetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
    }).then((res) => {
      if (!res.ok) throw new Error(`Cart request failed with status ${res.status}`);
      return res.json();
    });
  }

  function getCartWithXHR(asyncRequest = true) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      try {
        xhr.open('GET', '/cart.js', asyncRequest);
      } catch (e) {
        reject(e);
        return;
      }
      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) return;
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText || '{}'));
          } catch (e) {
            reject(e);
          }
        } else {
          reject(new Error(`Cart request failed with status ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error('Cart request failed due to network error'));
      try {
        xhr.send();
      } catch (e) {
        reject(e);
      }
    });
  }

  function getCartSync() {
    // 同期 XHR は非推奨だが API 互換のため残す
    const xhr = new XMLHttpRequest();
    try {
      xhr.open('GET', '/cart.js', false);
      xhr.send();
    } catch {
      throw new Error('Synchronous cart request failed');
    }
    if (xhr.status >= 200 && xhr.status < 300) {
      return JSON.parse(xhr.responseText || '{}');
    }
    throw new Error(`Cart request failed with status ${xhr.status}`);
  }

  /** ========================
   *  Core evaluation
   * =======================*/
  function evaluateCart(cart, context) {
    const attrs = cart && cart.attributes;
    const value = attrs ? attrs[REQUIRED_ATTRIBUTE] : undefined;
    if (!value) {
      // block
      const detail = {
        source: context.source || 'unknown',
        requiredAttribute: REQUIRED_ATTRIBUTE,
        redirectUrl: REDIRECT_URL,
      };
      if (context.submitButton?.classList) {
        context.submitButton.classList.remove('working');
      }
      safeEmit('cart:guard-blocked', detail);
      // リダイレクト（同期/非同期双方で同じ動作）
      const root = window.Shopify?.routes?.root || '/';
      const localizedRedirectUrl = (root + REDIRECT_URL).replace(/\/+/g, '/');
      window.location.href = localizedRedirectUrl;

      throw createBlockError();
    }
    // pass
    safeEmit('cart:guard-passed', {
      source: context.source || 'unknown',
      attribute: REQUIRED_ATTRIBUTE,
      value,
    });
    return cart;
  }

  function handleNonBlockError(context, error) {
    if (context.submitButton?.classList) {
      context.submitButton.classList.remove('working');
    }
    safeEmit('cart:guard-error', {
      source: context.source || 'unknown',
      message: error?.message || 'Unknown guard error',
    });
  }

  /** ========================
   *  Guard runners
   * =======================*/
  function runGuardAsync(context) {
    const loader = nativeFetch ? getCartWithFetch : () => getCartWithXHR(true);
    return loader()
    .then((cart) => evaluateCart(cart, context))
    .catch((err) => {
      if (err && err.__tfoGuard === GUARD_BLOCKED) throw err;
      handleNonBlockError(context, err);
      throw err;
    });
  }

  function runGuardSync(context) {
    try {
      const cart = getCartSync();
      return evaluateCart(cart, context);
    } catch (err) {
      if (err && err.__tfoGuard === GUARD_BLOCKED) throw err;
      handleNonBlockError(context, err);
      throw err;
    }
  }

  /** ========================
   *  Public API
   * =======================*/
  async function check(context = {}) {
    const preemptive = Boolean(context.preemptive);
    const result = await runGuardAsync(context);
    if (preemptive) guardState.skipNextIntercept = true;
    return result;
  }

  function checkSync(context = {}) {
    const preemptive = Boolean(context.preemptive);
    const result = runGuardSync(context);
    if (preemptive) guardState.skipNextIntercept = true;
    return result;
  }

  /** ========================
   *  Interceptors
   * =======================*/
  function applyFetchIntercept() {
    if (!nativeFetch) return;
    const wrapped = async function interceptedFetch(...args) {
      const url = getUrlFromInput(args[0]);
      const method = getMethodFromInputs(args[0], args[1]);
      if (shouldIntercept(url, method)) {
        if (guardState.skipNextIntercept) {
          guardState.skipNextIntercept = false;
        } else {
          await runGuardAsync({ source: 'fetch' });
        }
      }
      return nativeFetch(...args);
    };
    // 明示的に name を残すとデバッグしやすい（環境により無視されることも）
    try { Object.defineProperty(wrapped, 'name', { value: 'interceptedFetch' }); } catch {}
    window.fetch = wrapped;
  }

  function applyXHRIntercept() {
    XMLHttpRequest.prototype.open = function interceptedOpen(method, url, async, user, password) {
      this.__tfoGuard = {
        method: (typeof method === 'string' ? method : 'GET').toUpperCase(),
        url: toStr(url),
        async: async !== undefined ? async : true,
      };
      return nativeXHROpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function interceptedSend(body) {
      const g = this.__tfoGuard;
      if (g && shouldIntercept(g.url, g.method)) {
        if (g.async === false) {
          // 同期 XHR: ブロック時は送信しない
          try {
            runGuardSync({ source: 'xhr' });
            return nativeXHRSend.apply(this, arguments);
          } catch {
            return; // ブロックやエラー時は送らない
          }
        }
        // 非同期 XHR
        runGuardAsync({ source: 'xhr' })
        .then(() => nativeXHRSend.apply(this, arguments))
        .catch(() => { /* block or error: 送らない */ });
        return;
      }
      return nativeXHRSend.apply(this, arguments);
    };
  }

  /** ========================
   *  Init
   * =======================*/
  // カート追加後にチェックするため、追加前のインターセプトは無効化
  // applyFetchIntercept();
  // applyXHRIntercept();

  window.TFOCartGuard = {
    check,
    checkSync,
    requiredAttribute: REQUIRED_ATTRIBUTE,
    redirectUrl: REDIRECT_URL,
    version: VERSION,
  };
})();
